import * as Yup from "yup";
import {
  AccountPayer,
  ElectricityBlockValues,
  FormValues,
  HandoverTypeEnum,
  InternetBlockValues,
  PremiseAddressValues,
} from "../types";
import {
  PropertyType,
  ServiceTypeEnum,
  State,
} from "src/components/Premises/types";
import { getIsNmiRequired } from "../helpers";
import isValidABN from "is-valid-abn";
import {
  getAccountPayerField,
  hasTotalLoadDetails,
  parseDateString,
  validPhoneNumber,
  validatePlumberLicense,
} from "../helper";
import { Appliance, initialAppliances } from "./Calculator";
import { addBusinessDays, isBefore, startOfDay } from "date-fns";
import { levelSchema } from "src/schemas/LevelSchema";
import {
  PremiseComparable,
  OriginalPremiseFormat,
} from "src/components/Premises/BulkDuplicateDialog/types";

export const numberStringOnly = /^\d+$/;
export const alphanumericOnly = /^[a-zA-Z0-9]+$/;
export const accreditationNumberIntelliHubRegex = /^[a-zA-Z0-9]{1,6}$/;
export const accreditationNumberPlusESRegex = /^[a-zA-Z0-9]{1,8}$/;
// const hyphenatedAlphanumeric = /^[a-zA-Z0-9-]+$/;
const suburbRegex = /^[a-zA-Z0-9- ]+$/;
const numbersLettersHyphensSpacesNotStartWithHyphen =
  /^(?![- ])[a-zA-Z0-9- ]+$/;
const suburbRegexWithoutNumbers = /^[a-zA-Z- ]+$/;
const grannyFlatRegex = /^[0-9-]+[a-zA-Z]$/;
export const nameRegex = /^[a-zA-Z\s\-']+$/;
export const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const premiseDraftValidationSchema = Yup.object().shape({
  premiseAddressString: Yup.string().required("Please fill"),
  requestingCompany: Yup.mixed()
    .nullable()
    .test("is-required", "Please select", (value) => value !== null),
  selectedConnections: Yup.array()
    .min(1, "Please select at least one")
    .required("Please select at least one"),
  electricityCollaboratorId: Yup.string().when(
    ["assignElectricityRequirements", "selectedConnections"],
    {
      is: (
        assignElectricityRequirements: boolean,
        selectedConnections: string[]
      ) =>
        selectedConnections.includes("Electricity") &&
        assignElectricityRequirements,
      then: (schema) => schema.required("Please select"),
    }
  ),
  gasOptions: Yup.array().when("selectedConnections", {
    is: (selectedConnections: string[]) => selectedConnections.includes("Gas"),
    then: (schema) =>
      schema
        .min(1, "Please select at least one")
        .required("Please select at least one"),
  }),
  gasLine: Yup.object().shape({
    collaboratorId: Yup.string().when(
      ["assignRequirements", "$servicesSelected", "$gasOptions"],
      {
        is: (
          assignRequirements: boolean,
          servicesSelected: string[],
          gasOptions: string[]
        ) =>
          assignRequirements &&
          servicesSelected.includes("Gas") &&
          gasOptions.includes("gasLine"),
        then: (schema) => schema.required("Please select"),
      }
    ),
  }),
  gasMeter: Yup.object().shape({
    collaboratorId: Yup.string().when(
      ["assignRequirements", "$servicesSelected", "$gasOptions"],
      {
        is: (
          assignRequirements: boolean,
          servicesSelected: string[],
          gasOptions: string[]
        ) =>
          assignRequirements &&
          servicesSelected.includes("Gas") &&
          gasOptions.includes("gasMeter"),
        then: (schema) => schema.required("Please select"),
      }
    ),
  }),
  both: Yup.object().shape({
    collaboratorId: Yup.string().when(
      ["assignRequirements", "$servicesSelected", "$gasOptions"],
      {
        is: (
          assignRequirements: boolean,
          servicesSelected: string[],
          gasOptions: string[]
        ) =>
          assignRequirements &&
          servicesSelected.includes("Gas") &&
          gasOptions.includes("both"),
        then: (schema) => schema.required("Please select"),
      }
    ),
  }),
});

export const homeUserPremiseDraftValidationSchema = Yup.object().shape({
  premiseAddressString: Yup.string().required("Please fill"),
  selectedConnections: Yup.array()
    .min(1, "Please select at least one")
    .required("Please select at least one"),
  electricityCollaboratorId: Yup.string().when(
    ["assignElectricityRequirements", "selectedConnections"],
    {
      is: (
        assignElectricityRequirements: boolean,
        selectedConnections: string[]
      ) =>
        selectedConnections.includes("Electricity") &&
        assignElectricityRequirements,
      then: (schema) => schema.required("Please select"),
    }
  ),
  gasOptions: Yup.array().when("selectedConnections", {
    is: (selectedConnections: string[]) => selectedConnections.includes("Gas"),
    then: (schema) =>
      schema
        .min(1, "Please select at least one")
        .required("Please select at least one"),
  }),
  gasLine: Yup.object().shape({
    collaboratorId: Yup.string().when(
      ["assignRequirements", "$servicesSelected", "$gasOptions"],
      {
        is: (
          assignRequirements: boolean,
          servicesSelected: string[],
          gasOptions: string[]
        ) =>
          assignRequirements &&
          servicesSelected.includes("Gas") &&
          gasOptions.includes("gasLine"),
        then: (schema) => schema.required("Please select"),
      }
    ),
  }),
  gasMeter: Yup.object().shape({
    collaboratorId: Yup.string().when(
      ["assignRequirements", "$servicesSelected", "$gasOptions"],
      {
        is: (
          assignRequirements: boolean,
          servicesSelected: string[],
          gasOptions: string[]
        ) =>
          assignRequirements &&
          servicesSelected.includes("Gas") &&
          gasOptions.includes("gasMeter"),
        then: (schema) => schema.required("Please select"),
      }
    ),
  }),
  both: Yup.object().shape({
    collaboratorId: Yup.string().when(
      ["assignRequirements", "$servicesSelected", "$gasOptions"],
      {
        is: (
          assignRequirements: boolean,
          servicesSelected: string[],
          gasOptions: string[]
        ) =>
          assignRequirements &&
          servicesSelected.includes("Gas") &&
          gasOptions.includes("both"),
        then: (schema) => schema.required("Please select"),
      }
    ),
  }),
});

export const premiseAddressDraftValidationSchema = (
  premiseValues: FormValues
) =>
  Yup.object().shape(
    {
      state: Yup.string()
        .matches(alphanumericOnly)
        .min(2)
        .max(3)
        .required("Required"),
      buildingName: Yup.string(),
      suburb: Yup.string()
        .matches(suburbRegexWithoutNumbers, "Letters, hyphens and spaces only")
        .required("Letters, hyphens and spaces only"),
      postcode: Yup.string()
        .matches(numberStringOnly, "Numbers only")
        .min(4, "Postcode must be at least 4 characters")
        .max(4)
        .required("Numbers only"),
      unitNumber: Yup.string().when("premiseType", {
        is: (n: string) =>
          n && premiseValues.propertyType === PropertyType.COMMERCIAL,
        then: (schema) =>
          schema
            .matches(alphanumericOnly, "Numbers and letters only")
            .required("Numbers and letters only"),
        otherwise: (schema) =>
          schema.matches(alphanumericOnly, "Numbers and letters only"),
      }),
      premiseType: Yup.string().when("unitNumber", {
        is: (n: string) =>
          n && premiseValues.propertyType === PropertyType.COMMERCIAL,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.matches(alphanumericOnly),
      }),
      blockNumber: Yup.string().when(["streetNumber", "state"], {
        is: (s: string, state: string) => !s && state === "ACT",
        then: (schema) =>
          schema
            .matches(numberStringOnly, "Numbers only")
            .required("Numbers only"),
      }),
      sectionNumber: Yup.string().when("state", {
        is: (state: string) => state === "ACT",
        then: (schema) =>
          schema
            .matches(numberStringOnly, "Numbers only")
            .required("Numbers only"),
        otherwise: (schema) => schema.matches(numberStringOnly, "Numbers only"),
      }),
      streetNumber: Yup.string()
        .when(["state"], {
          is: (state: string) => state === State.NSW,
          then: (schema) =>
            schema
              .optional()
              .matches(
                numbersLettersHyphensSpacesNotStartWithHyphen,
                "Numbers, letters, hyphens and spaces only"
              ),
        })
        .when(["lotNumber", "grannyFlat", "state"], {
          is: (lotNumber: string, grannyFlat: boolean, state: string) =>
            !lotNumber && !grannyFlat && state !== State.NSW,
          then: (schema) =>
            schema
              .matches(
                numbersLettersHyphensSpacesNotStartWithHyphen,
                "Numbers, letters, hyphens and spaces only"
              )
              .required("Numbers, letters, hyphens and spaces only"),
          otherwise: (schema) =>
            schema.matches(
              numbersLettersHyphensSpacesNotStartWithHyphen,
              "Numbers, letters, hyphens and spaces only"
            ),
        })
        .when(["grannyFlat"], {
          is: (grannyFlat: boolean) => grannyFlat,
          then: (schema) =>
            schema
              .matches(
                grannyFlatRegex,
                "Granny flat requires street number with suffix, e.g. 12A"
              )
              .required("Required"),
          otherwise: (schema) =>
            schema.matches(
              numbersLettersHyphensSpacesNotStartWithHyphen,
              "Numbers, letters, hyphens and spaces only"
            ),
        })
        .when(["state", "blockNumber"], {
          is: (state: string, blockNumber: string) =>
            state === State.ACT && blockNumber,
          then: (schema) =>
            schema
              .optional()
              .matches(
                numbersLettersHyphensSpacesNotStartWithHyphen,
                "Numbers, letters, hyphens and spaces only"
              ),
        }),
      lotNumber: Yup.string()
        .when("state", {
          is: (state: State) => state === State.NSW,
          then: (schema) => schema.required("Numbers and letters only"),
        })
        .when(["streetNumber", "state"], {
          is: (streetNumber: string, state: State) =>
            state !== State.ACT && !streetNumber,
          then: (schema) =>
            schema
              .matches(alphanumericOnly, "Numbers and letters only")
              .required("Numbers and letters only"),
          otherwise: (schema) =>
            schema.matches(alphanumericOnly, "Numbers and letters only"),
        }),
      streetName: Yup.string()
        .matches(suburbRegex, "Numbers, letters, hyphens and spaces only")
        .required("Numbers, letters, hyphens and spaces only"),
      grannyFlat: Yup.boolean(),
      level: levelSchema,
      dpNumber: Yup.string()
        .when("state", {
          is: (state: string) => state === "NSW",
          then: (schema) =>
            schema
              .matches(numberStringOnly, "Numbers only")
              .required("Numbers only"),
        })
        .when("state", {
          is: (state: string) => state === "ACT",
          then: (schema) =>
            schema
              .transform((value) => (value === "" ? null : value))
              .nullable()
              .matches(numberStringOnly, "Numbers only"),
        }),
      streetSuffix: Yup.string(),
      streetType: Yup.string().required("Required"),
    },
    [
      ["lotNumber", "streetNumber"],
      ["unitNumber", "premiseType"],
      ["streetNumber", "blockNumber"],
    ]
  );

export const premiseValidationSchema = {
  siteSupervisor: Yup.string(),
  projectName: Yup.string(),
  propertyType: Yup.string(),
  propertyAccess: Yup.string(),
  preventingAccess: Yup.string(),
  serviceAssignee: Yup.string(),
};

//TODO: not used for now, may use for indexing in the future
export const premiseInitialValues = {
  id: null,
  siteSupervisor: "",
  requestingCompany: null,
  projectName: "",
  projectId: "",
  propertyType: "RESIDENTIAL",
  premiseAddress: {
    state: "",
    buildingName: "",
    suburb: "",
    postcode: "",
    unitNumber: "",
    premiseType: "",
    streetNumber: "",
    lotNumber: "",
    streetName: "",
    grannyFlat: false,
    level: "",
    dpNumber: "",
    streetSuffix: "",
    streetType: "",
    sectionNumber: "",
    blockNumber: "",
  },
  propertyAccess: "yes",
  preventingAccess: "",
  electricityAccountPayer: AccountPayer.COMPANY,
  internetAccountPayer: AccountPayer.COMPANY,
  assignElectricityRequirements: false,
  assignGasRequirements: false,
  serviceAssignee: "",
  selectedConnections: [],
};

export const premiseAddressInitialValues = {
  state: "",
  buildingName: "",
  suburb: "",
  postcode: "",
  unitNumber: "",
  premiseType: "",
  streetNumber: "",
  lotNumber: "",
  streetName: "",
  grannyFlat: false,
  level: "",
  dpNumber: "",
  streetSuffix: "",
  streetType: "",
  sectionNumber: "",
  blockNumber: "",
};

const onlineFormAccountDetailsValidationSchemaFields = () => ({
  accountPrimaryFirstName: Yup.string()
    .trim()
    .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
    .required("Letters, hyphens, spaces and apostrophes only"),
  accountPrimaryLastName: Yup.string()
    .trim()
    .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
    .required("Letters, hyphens, spaces and apostrophes only"),
  accountPrimaryEmail: Yup.string()
    .matches(emailRegex, "Please enter a valid email address")
    .required("Please enter a valid email address"),
});

export const onlineFormAccountDetailsValidationSchema = () =>
  Yup.object().shape({
    ...onlineFormAccountDetailsValidationSchemaFields(),
  });

export const homeUserAccountValidationSchemaFields = (
  blockType: string,
  claim: boolean = false,
  isAccountReadOnly?: boolean,
  isDigitalEic?: boolean
) => ({
  accountHolder: isDigitalEic
    ? Yup.string().nullable()
    : Yup.string().required(),
  companyName: Yup.string().when(["accountHolder"], {
    is: (type: string) => type === "company" && !isDigitalEic,
    then: (schema) => schema.required("Please fill"),
  }),
  abn: Yup.string().when(["accountHolder"], {
    is: (type: string) => type === "company" && !isDigitalEic,
    then: (schema) =>
      schema
        .test({
          message: "Please enter a valid 11 digit ABN number",
          test: (value) => isValidABN(value as string),
        })
        .required("Please enter a valid 11 digit ABN number"),
  }),
  accountPrimaryFirstName: Yup.string().when(
    ["accountHolder", getAccountPayerField(blockType)],
    {
      is: (holder: string, payer: string) =>
        (payer === AccountPayer.SOMEONE_ELSE &&
          (holder === "company" || holder === "person")) ||
        (payer === AccountPayer.COMPANY && holder === "company"),
      then: (schema) =>
        schema
          .trim()
          .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
          .required("Letters, hyphens, spaces and apostrophes only"),
    }
  ),
  accountPrimaryLastName: Yup.string().when(
    ["accountHolder", getAccountPayerField(blockType)],
    {
      is: (holder: string, payer: string) =>
        (payer === AccountPayer.SOMEONE_ELSE &&
          (holder === "company" || holder === "person")) ||
        (payer === AccountPayer.COMPANY && holder === "company"),
      then: (schema) =>
        schema
          .trim()
          .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
          .required("Letters, hyphens, spaces and apostrophes only"),
    }
  ),
  customerConsentFiles: Yup.array().when([getAccountPayerField(blockType)], {
    is: (payer: string) =>
      payer === AccountPayer.SOMEONE_ELSE &&
      !claim &&
      !isAccountReadOnly &&
      !isDigitalEic,
    then: (schema) =>
      schema
        .min(1, "Please upload a document")
        .required("Please upload a document"),
    otherwise: (schema) => schema.optional(),
  }),
  claimsCustomerConsentFiles: Yup.array().when(
    [getAccountPayerField(blockType)],
    {
      is: (payer: string) =>
        payer === AccountPayer.SOMEONE_ELSE && claim === true && !isDigitalEic,
      then: (schema) =>
        schema
          .min(1, "Please upload a document")
          .required("Please upload a document"),
    }
  ),
  accountPrimaryDob: isDigitalEic
    ? Yup.date().nullable()
    : Yup.date().required("Date format should be DD/MM/YYYY"),
  accountPrimaryPhone: isDigitalEic
    ? Yup.string().nullable()
    : Yup.string()
        .test({
          message: "Please enter a valid 10 digit phone number",
          test: (value) => validPhoneNumber(value as string),
        })
        .matches(numberStringOnly, "Please enter a valid 10 digit phone number")
        .required("Please enter a valid 10 digit phone number"),
  accountPrimaryEmail: Yup.string().when(
    ["accountHolder", getAccountPayerField(blockType)],
    {
      is: (holder: string, payer: string) =>
        (payer === AccountPayer.SOMEONE_ELSE &&
          (holder === "company" || holder === "person")) ||
        (payer === AccountPayer.COMPANY && holder === "company"),
      then: (schema) =>
        schema
          .matches(emailRegex, "Please enter a valid email address")
          .required("Please enter a valid email address"),
      otherwise: (schema) => schema.nullable(),
    }
  ),
  billingAddressString: Yup.string().when(["sameAsConnectionAddress"], {
    is: (same: boolean) => !same && !isDigitalEic,
    then: (schema) => schema.required("Please enter a billing address"),
  }),
  secondaryAccountHolder: Yup.boolean(),
  accountSecondaryFirstName: Yup.string()
    .trim()
    .when("secondaryAccountHolder", {
      is: (type: boolean) => type === true,
      then: (schema) =>
        schema
          .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
          .required("Letters, hyphens, spaces and apostrophes only"),
    }),
  accountSecondaryLastName: Yup.string()
    .trim()
    .when("secondaryAccountHolder", {
      is: (type: boolean) => type === true,
      then: (schema) =>
        schema
          .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
          .required("Letters, hyphens, spaces and apostrophes only"),
    }),
  accountSecondaryDob: Yup.date().when("secondaryAccountHolder", {
    is: (type: boolean) => type === true,
    then: (schema) => schema.required("Date format should be DD/MM/YYYY"),
    otherwise: (schema) => schema.nullable(),
  }),
  accountSecondaryPhone: Yup.string().when("secondaryAccountHolder", {
    is: (type: boolean) => type === true,
    then: (schema) =>
      schema
        .test({
          message: "Please enter a valid 10 digit phone number",
          test: (value) => validPhoneNumber(value as string),
        })
        .matches(numberStringOnly, "Please enter a valid 10 digit phone number")
        .required("Please enter a valid 10 digit phone number"),
  }),
  accountSecondaryEmail: Yup.string().when("secondaryAccountHolder", {
    is: (type: boolean) => type === true,
    then: (schema) =>
      schema
        .matches(emailRegex, "Please enter a valid email address")
        .required("Please enter a valid email address"),
  }),
});

export const accountValidationSchemaFields = (
  blockType: string,
  claim: boolean = false,
  isAccountReadOnly?: boolean,
  isDigitalEic?: boolean | false
) => ({
  accountHolder: isDigitalEic
    ? Yup.string().nullable()
    : Yup.string().required(),
  companyName: Yup.string().when(
    ["accountHolder", getAccountPayerField(blockType)],
    {
      is: (type: string, payer: string) =>
        payer === AccountPayer.SOMEONE_ELSE &&
        type === "company" &&
        !isDigitalEic,
      then: (schema) => schema.required("Please fill"),
    }
  ),
  abn: Yup.string().when(["accountHolder", getAccountPayerField(blockType)], {
    is: (type: string, payer: string) =>
      payer === AccountPayer.SOMEONE_ELSE &&
      type === "company" &&
      !isDigitalEic,
    then: (schema) =>
      schema
        .test({
          message: "Please enter a valid 11 digit ABN number",
          test: (value) => isValidABN(value as string),
        })
        .required("Please enter a valid 11 digit ABN number"),
  }),
  accountPrimaryFirstName: Yup.string()
    .trim()
    .when([getAccountPayerField(blockType)], {
      is: (payer: string) => payer === AccountPayer.SOMEONE_ELSE,
      then: (schema) =>
        schema
          .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
          .required("Letters, hyphens, spaces and apostrophes only"),
    }),
  accountPrimaryLastName: Yup.string()
    .trim()
    .when([getAccountPayerField(blockType)], {
      is: (payer: string) => payer === AccountPayer.SOMEONE_ELSE,
      then: (schema) =>
        schema
          .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
          .required("Letters, hyphens, spaces and apostrophes only"),
    }),
  accountPrimaryDob: Yup.date().when(
    [getAccountPayerField(blockType), "accountHolder"],
    {
      is: (payer: string) =>
        payer === AccountPayer.SOMEONE_ELSE && !isDigitalEic,
      then: (schema) => schema.required("Date format should be DD/MM/YYYY"),
      otherwise: (schema) => schema.nullable(),
    }
  ),
  accountPrimaryPhone: Yup.string().when([getAccountPayerField(blockType)], {
    is: (payer: string) => payer === AccountPayer.SOMEONE_ELSE && !isDigitalEic,
    then: (schema) =>
      schema
        .test({
          message: "Please enter a valid 10 digit phone number",
          test: (value) => validPhoneNumber(value as string),
        })
        .matches(numberStringOnly, "Please enter a valid 10 digit phone number")
        .required("Please enter a valid 10 digit phone number"),
  }),
  accountPrimaryEmail: Yup.string().when([getAccountPayerField(blockType)], {
    is: (payer: string) => payer === AccountPayer.SOMEONE_ELSE,
    then: (schema) =>
      schema
        .matches(emailRegex, "Please enter a valid email address")
        .required("Please enter a valid email address"),
  }),
  customerConsentFiles: Yup.array().when([getAccountPayerField(blockType)], {
    is: (payer: string) =>
      payer === AccountPayer.SOMEONE_ELSE &&
      claim === false &&
      !isAccountReadOnly &&
      !isDigitalEic,
    then: (schema) =>
      schema
        .min(1, "Please upload a document")
        .required("Please upload a document"),
  }),
  claimsCustomerConsentFiles: Yup.array().when(
    [getAccountPayerField(blockType)],
    {
      is: (payer: string) =>
        payer === AccountPayer.SOMEONE_ELSE && claim === true && !isDigitalEic,
      then: (schema) =>
        schema
          .min(1, "Please upload a document")
          .required("Please upload a document"),
    }
  ),
  billingAddressString: Yup.string().when(
    [getAccountPayerField(blockType), "sameAsConnectionAddress"],
    {
      is: (payer: string, same: boolean) =>
        !same && payer === AccountPayer.SOMEONE_ELSE && !isDigitalEic,
      then: (schema) => schema.required("Please enter a billing address"),
    }
  ),
  secondaryAccountHolder: Yup.boolean(),
  accountSecondaryFirstName: Yup.string()
    .trim()
    .when("secondaryAccountHolder", {
      is: (type: boolean) => type === true,
      then: (schema) =>
        schema
          .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
          .required("Letters, hyphens, spaces and apostrophes only"),
    }),
  accountSecondaryLastName: Yup.string()
    .trim()
    .when("secondaryAccountHolder", {
      is: (type: boolean) => type === true,
      then: (schema) =>
        schema
          .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
          .required("Letters, hyphens, spaces and apostrophes only"),
    }),
  accountSecondaryDob: Yup.date().when("secondaryAccountHolder", {
    is: (type: boolean) => type === true,
    then: (schema) => schema.required("Date format should be DD/MM/YYYY"),
    otherwise: (schema) => schema.nullable(),
  }),
  accountSecondaryPhone: Yup.string().when("secondaryAccountHolder", {
    is: (type: boolean) => type === true,
    then: (schema) =>
      schema
        .test({
          message: "Please enter a valid 10 digit phone number",
          test: (value) => validPhoneNumber(value as string),
        })
        .matches(numberStringOnly, "Please enter a valid 10 digit phone number")
        .required("Please enter a valid 10 digit phone number"),
  }),
  accountSecondaryEmail: Yup.string().when("secondaryAccountHolder", {
    is: (type: boolean) => type === true,
    then: (schema) =>
      schema
        .matches(emailRegex, "Please enter a valid email address")
        .required("Please enter a valid email address"),
  }),
});

export const accountResidentialValidationSchema = Yup.object().shape({
  accountHolder: Yup.string().required(),
  secondaryAccountHolder: Yup.boolean(),
  accountSecondaryFirstName: Yup.string().when("secondaryAccountHolder", {
    is: (type: boolean) => type === true,
    then: (schema) => schema.matches(alphanumericOnly).required(),
  }),
  accountSecondaryLastName: Yup.string().when("accountHolder", {
    is: (type: boolean) => type === true,
    then: (schema) => schema.matches(alphanumericOnly).required(),
  }),
  accountSecondaryDob: Yup.date().when("accountHolder", {
    is: (type: boolean) => type === true,
    then: (schema) => schema.required(),
  }),
  accountSecondaryPhone: Yup.string().when("accountHolder", {
    is: (type: boolean) => type === true,
    then: (schema) =>
      schema
        .test({
          message: "Invalid Phone Number",
          test: (value) => validPhoneNumber(value as string),
        })
        .matches(numberStringOnly, "Invalid Phone Number")
        .required("Invalid Phone Number"),
  }),
  accountSecondaryEmail: Yup.string().when("accountHolder", {
    is: (type: boolean) => type === true,
    then: (schema) => schema.email("Check email address").required(),
  }),
});

export const billingAddressValidationSchema = Yup.object().shape(
  {
    suburb: Yup.string()
      .matches(suburbRegexWithoutNumbers, "Letters, hyphens and spaces only")
      .required("Letters, hyphens and spaces only"),
    state: Yup.string()
      .matches(alphanumericOnly)
      .min(2)
      .max(3)
      .required("Required"),
    postcode: Yup.string()
      .matches(numberStringOnly, "Numbers only")
      .min(4, "Postcode must be at least 4 characters")
      .max(4)
      .required("Numbers only"),
    pobox: Yup.boolean(),
    unitNumber: Yup.string().when("premiseType", {
      is: (n: string) => n,
      then: (schema) =>
        schema
          .matches(alphanumericOnly, "Numbers and letters only")
          .required("Numbers and letters only"),
      otherwise: (schema) =>
        schema.matches(alphanumericOnly, "Numbers and letters only"),
    }),
    premiseType: Yup.string().when("unitNumber", {
      is: (n: string) => n,
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.matches(alphanumericOnly),
    }),
    streetNumber: Yup.string().when("pobox", {
      is: (p: boolean) => !p,
      then: (schema) =>
        schema
          .matches(
            numbersLettersHyphensSpacesNotStartWithHyphen,
            "Numbers, letters, hyphens and spaces only"
          )
          .required("Numbers, letters, hyphens and spaces only"),
    }),
    streetName: Yup.string().when("pobox", {
      is: (p: boolean) => !p,
      then: (schema) =>
        schema
          .matches(suburbRegex, "Numbers, letters, hyphens and spaces only")
          .required("Numbers, letters, hyphens and spaces only"),
    }),
    level: levelSchema,
    streetSuffix: Yup.string(),
    streetType: Yup.string().when("pobox", {
      is: (p: boolean) => !p,
      then: (schema) => schema.required("Required"),
    }),
    poboxType: Yup.string().when("pobox", {
      is: (p: boolean) => p,
      then: (schema) => schema.required("Required"),
    }),
    poboxNumber: Yup.string().when(["pobox", "poboxType"], {
      is: (p: boolean, type: string) =>
        p && determineIfPoBoxNumberIsRequired(type),
      then: (schema) =>
        schema
          .matches(numberStringOnly, "Numbers only")
          .required("Numbers only"),
    }),
  },
  [["unitNumber", "premiseType"]]
);

const determineIfPoBoxNumberIsRequired = (poBoxType: string): boolean => {
  if (poBoxType === "carePo" || poBoxType === "CMA" || poBoxType === "CPA") {
    return false;
  }
  return true;
};

export const electricityInitialValues: ElectricityBlockValues = {
  licensedElectrician: "",
  licensedElectricianDetails: null,
  aspDetails: null,
  connectionType: null,
  meterProvider: "",
  asp: "",
  maxLoad: "",
  phases: "",
  transformer: false,
  showAsp: false,
  moreThan100Consumption: false,
  truckAppointment: false,
  truckAppointmentReason: "",
  nmi: "",
  installationType: "",
  locationDescription: "",
  otherLocationDescription: "",
  solar: "no",
  sizeOfSolarSystem: "",
  offpeakRequirements: [],
  otherOffpeakRequirement: "",
  rexId: "",
  coes: "",
  ntc: "",
  state: "",
  isNmiRequired: false,
  coesFiles: [],
  mcoesFiles: [],
  ewrFiles: [],
  //premise-block-electricity
  assignElectricityRequirements: false,
  electricityAccountPayer: AccountPayer.COMPANY,
  electricityCollaboratorId: "",
  electricityLifeSupport: undefined,
  homeUserLicencedElectrician: {
    firstName: "",
    lastName: "",
    companyName: "",
    phoneNumber: "",
    email: "",
    licenseNumber: "",
  },
  homeUserAsp: {
    firstName: "",
    lastName: "",
    licenseNumber: "",
  },
};

export const internetInitValues: InternetBlockValues = {
  // connectionType: null,
  //premise-block-internet
  assignInternetRequirements: false,
  internetAccountPayer: AccountPayer.COMPANY,
  internetCollaboratorId: "",
  internetHandoverDate: "",
  internetPreinstallationDate: "",
  internetRequesterEmail: "",
  internetRequesterFirstName: "",
  internetRequesterLastName: "",
  internetRequesterName: "",
  internetConnectionType: null,
};

export const gasInitialValues = {
  //premise-block-gas
  gasOptions: [],
  gasLine: {
    assignRequirements: false,
    accountPayer: AccountPayer.COMPANY,
    collaboratorId: "",
    buildingType: "new",
    licensedPlumber: "",
    siteLockupDate: null,
    pressure: "",
    otherPressure: "",
    totalLoad: "",
    totalLoadSummary: "",
    totalLoadDetails: initialAppliances,
    siteConditions: [],
    otherSiteCondition: "",
    meterLocation: "",
    otherMeterLocation: "",
    appliancesChecklistFiles: [],
    sitePlanFiles: [],
  },
  gasMeter: {
    assignRequirements: false,
    accountPayer: AccountPayer.COMPANY,
    collaboratorId: "",
    licensedPlumber: "",
    mirn: "",
    preferDate: null,
    preferTime: "",
    access: "",
    cocNumber: "",
    swnNumber: "",
    isUpdatesOnGasLine: "no",
    //same as gasLine/both
    pressure: "",
    otherPressure: "",
    totalLoad: "",
    totalLoadSummary: "",
    totalLoadDetails: initialAppliances,
    siteConditions: [],
    otherSiteCondition: "",
    meterLocation: "",
    otherMeterLocation: "",
    appliancesChecklistFiles: [],
    sitePlanFiles: [],
  },
  both: {
    assignRequirements: false,
    accountPayer: AccountPayer.COMPANY,
    collaboratorId: "",
    buildingType: "new",
    licensedPlumber: "",
    pressure: "",
    otherPressure: "",
    totalLoad: "",
    totalLoadSummary: "",
    totalLoadDetails: initialAppliances,
    siteConditions: [],
    otherSiteCondition: "",
    meterLocation: "",
    otherMeterLocation: "",
    appliancesChecklistFiles: [],
    sitePlanFiles: [],
  },
};

export const closeTransferInitialValues = {
  handoverType: HandoverTypeEnum.CLOSE_AND_TRANSFER,
  serviceTypes: [],
  gasPreferDate: null,
  elecPreferDate: null,
  signedTransferFormFiles: [],
  fileMeta: undefined,
  accountHolder: undefined,
  isCustomerConsentChecked: false,
  isShowCustomerConsentCheckbox: true,
  isAccountHolderEditable: true,
};

const totalLoadSchema = Yup.string().when("$propertyType", {
  is: (propertyType: string) => propertyType === PropertyType.COMMERCIAL,
  then: (schema) =>
    schema.matches(numberStringOnly, "Numbers only").required("Numbers only"),
  otherwise: (schema) =>
    schema
      .required(
        "Please click on calculator icon to provide gas appliance details."
      )
      .matches(
        numberStringOnly,
        "Please click on calculator icon to provide gas appliance details."
      )
      .when("totalLoadDetails", {
        is: (totalLoadDetails: Appliance[]) =>
          !hasTotalLoadDetails(totalLoadDetails),
        then: () =>
          schema.test(
            "always-show-error",
            "Please click on calculator icon to provide gas appliance details.",
            () => false
          ),
      }),
});

export const gasLineValidationBaseSchema = (homeUser?: boolean) => ({
  siteLockupDate: Yup.mixed().when("$state", {
    is: (state: string) => state === "VIC",
    then: (schema) =>
      schema
        .nullable()
        .required("Please enter")
        .test(
          "is-valid-date",
          "Please enter in correct format DD/MM/YYYY",
          (value) => {
            if (value === null) return false; // Fail if null (required)
            return value instanceof Date && !isNaN(value.getTime());
          }
        )
        .test(
          "is-not-past-date",
          "The date should be today's date or a future date",
          (value) => {
            if (value === null) return false; // Fail if null (required)
            const today = startOfDay(new Date());
            return value instanceof Date && !isBefore(value, today);
          }
        ),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  pressure: Yup.string().required("Please select"),
  otherPressure: Yup.string().when("pressure", {
    is: "0",
    then: (schema) =>
      schema.matches(numberStringOnly, "Numbers only").required("Numbers only"),
  }),
  totalLoad: totalLoadSchema,
  siteConditions: Yup.array().when(
    ["$state", "$gasLine.accountPayer", "$homeUser"],
    {
      is: (state: string, gasLineAccountPayer: string) =>
        state === "ACT" ||
        (state === "NSW" &&
          gasLineAccountPayer !== AccountPayer.COMPANY &&
          !homeUser),
      then: (schema) => schema.min(1, "Please select"),
    }
  ),
  otherSiteCondition: Yup.string().when("siteConditions", {
    is: (siteConditions: string[]) => siteConditions.includes("other"),
    then: (schema) => schema.required("Please fill"),
  }),
  meterLocation: Yup.string().when("$state", {
    is: (state: string) => ["NSW", "ACT"].includes(state),
    then: (schema) => schema.required("Please select"),
  }),
  otherMeterLocation: Yup.string().when("meterLocation", {
    is: "other",
    then: (schema) => schema.required("Please fill"),
  }),
  appliancesChecklistFiles: Yup.array().when("$propertyType", {
    is: (propertyType: string) => propertyType === "COMMERCIAL",
    then: (schema) =>
      schema
        .min(1, "Please upload a document")
        .required("Please upload a document"),
  }),
  sitePlanFiles: Yup.array().when("$propertyType", {
    is: (propertyType: string) => propertyType === "COMMERCIAL",
    then: (schema) =>
      schema
        .min(1, "Please upload a document")
        .required("Please upload a document"),
  }),
});

export const gasBothValidationBaseSchema = (homeUser?: boolean) => ({
  pressure: Yup.string().required("Please select"),
  otherPressure: Yup.string().when("pressure", {
    is: "0",
    then: (schema) =>
      schema.matches(numberStringOnly, "Numbers only").required("Numbers only"),
  }),
  totalLoad: totalLoadSchema,

  siteConditions: Yup.array().when(["$state", "$both.accountPayer"], {
    is: (state: string, bothAccountPayer: string) =>
      state === "ACT" ||
      (state === "NSW" &&
        !homeUser &&
        bothAccountPayer !== AccountPayer.COMPANY),
    then: (schema) => schema.min(1, "Please select"),
  }),
  otherSiteCondition: Yup.string().when("siteConditions", {
    is: (siteConditions: string[]) => siteConditions.includes("other"),
    then: (schema) => schema.required("Please fill"),
  }),
  meterLocation: Yup.string().when("$state", {
    is: (state: string) => ["NSW", "ACT"].includes(state),
    then: (schema) => schema.required("Please select"),
  }),
  otherMeterLocation: Yup.string().when("meterLocation", {
    is: "other",
    then: (schema) => schema.required("Please fill"),
  }),
  appliancesChecklistFiles: Yup.array().when("$propertyType", {
    is: (propertyType: string) => propertyType === "COMMERCIAL",
    then: (schema) =>
      schema
        .min(1, "Please upload a document")
        .required("Please upload a document"),
  }),
  sitePlanFiles: Yup.array().when("$propertyType", {
    is: (propertyType: string) => propertyType === "COMMERCIAL",
    then: (schema) =>
      schema
        .min(1, "Please upload a document")
        .required("Please upload a document"),
  }),
  lifeSupport: Yup.boolean()
    .nullable()
    .transform((value) => (value === undefined ? null : value))
    .default(null)
    .when(["$both.accountPayer", "$state"], {
      is: (bothAccountPayer: string, state: string) =>
        bothAccountPayer === AccountPayer.COMPANY &&
        !homeUser &&
        state === State.VIC,
      then: (schema) =>
        schema.required(
          "Oops, you must let us know if someone at the property is  on life support."
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
});

export const gasMeterValidationBaseSchema = (homeUser?: boolean) => ({
  mirn: Yup.string().when("$state", {
    is: (state: string) => state === "SA",
    then: (schema) =>
      schema
        .required("Please fill")
        .matches(/^\d+$/, "Numbers only")
        .test("is-10-digits", "10 digits expected", (value) =>
          value ? value.length === 10 : false
        ),
  }),
  preferDate: Yup.mixed().when("$state", {
    is: (state: string) => state === "SA",
    then: (schema) =>
      schema
        .nullable()
        .required("Please enter")
        .test(
          "is-valid-date",
          "Please enter in correct format DD/MM/YYYY",
          (value) => {
            if (value === null) return false; // Treat null as empty
            return value instanceof Date && !isNaN(value.getTime());
          }
        )
        .test(
          "is-not-available-date",
          "The date should be 3 working days from today",
          (value) => {
            if (value === null) return false; // Treat null as empty
            const today = startOfDay(new Date());
            const threeWorkingDaysFromNow = addBusinessDays(today, 3);
            return (
              value instanceof Date && !isBefore(value, threeWorkingDaysFromNow)
            );
          }
        ),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  preferTime: Yup.string().when("$state", {
    is: (state: string) => state === "SA",
    then: (schema) => schema.required("Please select"),
  }),
  cocNumber: Yup.string().when("$showCocNumberField", {
    is: true,
    then: (schema) =>
      schema.matches(numberStringOnly, "Numbers only").required("Numbers only"),
  }),
  swnNumber: Yup.string().when("$showSwnNumberField", {
    is: true,
    then: (schema) => schema.required("Please fill").matches(numberStringOnly),
  }),
  pressure: Yup.string().required("Please select"),
  otherPressure: Yup.string().when("pressure", {
    is: "0",
    then: (schema) =>
      schema.matches(numberStringOnly, "Numbers only").required("Numbers only"),
  }),
  totalLoad: totalLoadSchema,

  siteConditions: Yup.array().when(["$state", "$gasMeter.accountPayer"], {
    is: (state: string, gasMeterAccountPayer: string) =>
      state === "ACT" ||
      (state === "NSW" &&
        !homeUser &&
        gasMeterAccountPayer !== AccountPayer.COMPANY),
    then: (schema) => schema.min(1, "Please select"),
  }),
  otherSiteCondition: Yup.string().when("siteConditions", {
    is: (siteConditions: string[]) => siteConditions.includes("other"),
    then: (schema) => schema.required("Please fill"),
  }),
  meterLocation: Yup.string().when("$state", {
    is: (state: string) => ["NSW", "ACT"].includes(state),
    then: (schema) => schema.required("Please select"),
  }),
  otherMeterLocation: Yup.string().when("meterLocation", {
    is: "other",
    then: (schema) => schema.required("Please fill"),
  }),
  appliancesChecklistFiles: Yup.array().when("$propertyType", {
    is: (propertyType: string) => propertyType === "COMMERCIAL",
    then: (schema) =>
      schema
        .min(1, "Please upload a document")
        .required("Please upload a document"),
  }),
  sitePlanFiles: Yup.array().when("$propertyType", {
    is: (propertyType: string) => propertyType === "COMMERCIAL",
    then: (schema) =>
      schema
        .min(1, "Please upload a document")
        .required("Please upload a document"),
  }),
  lifeSupport: Yup.boolean()
    .nullable()
    .transform((value) => (value === undefined ? null : value))
    .default(null)
    .when(["$gasMeter.accountPayer", "$state"], {
      is: (gasMeterAccountPayer: string, state: string) =>
        gasMeterAccountPayer === AccountPayer.COMPANY &&
        !homeUser &&
        state === State.VIC,
      then: (schema) =>
        schema.required(
          "Oops, you must let us know if someone at the property is  on life support."
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
});

export const electricitySubmissionWithCompanyFields = {
  licensedElectrician: Yup.string().required("Please select"),
  asp: Yup.string().when("showAsp", {
    is: true,
    then: (schema) => schema.required("Please select"),
  }),
};

export const electricitySubmissionHomeUserFields = {
  homeUserLicencedElectrician: Yup.object().shape({
    firstName: Yup.string()
      .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
      .required("Letters, hyphens, spaces and apostrophes only"),
    lastName: Yup.string()
      .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
      .required("Letters, hyphens, spaces and apostrophes only"),
    licenseNumber: Yup.string()
      .matches(alphanumericOnly, "Numbers and letters only")
      .required("Numbers and letters only"),
    companyName: Yup.string().optional(),
    phoneNumber: Yup.string()
      .test({
        message: "Please enter a valid 10 digit phone number",
        test: (value) => validPhoneNumber(value as string),
      })
      .matches(numberStringOnly, "Please enter a valid 10 digit phone number")
      .required("Please enter a valid 10 digit phone number"),
    email: Yup.string().email("Please enter a valid email address").optional(),
  }),
  homeUserAsp: Yup.mixed().when("showAsp", {
    is: true,
    then: () =>
      Yup.object().shape({
        firstName: Yup.string()
          .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
          .required("Letters, hyphens, spaces and apostrophes only"),
        lastName: Yup.string()
          .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
          .required("Letters, hyphens, spaces and apostrophes only"),
        licenseNumber: Yup.string()
          .when("$meterProvider", {
            is: (meterProvider: string) => meterProvider === "ACUMENMPB",
            then: (schema) =>
              schema
                .matches(
                  accreditationNumberIntelliHubRegex,
                  "Can have no more than 6 letters or digits"
                )
                .required("Please fill"),
          })
          .when("$meterProvider", {
            is: (meterProvider: string) => meterProvider === "TCAMPMPB",
            then: (schema) =>
              schema
                .matches(
                  accreditationNumberPlusESRegex,
                  "Can have no more than 8 letters or digit"
                )
                .required("Please fill"),
          }),
      }),
    otherwise: () => Yup.mixed().notRequired(),
  }),
  connectionType: Yup.string().required("Please select"),
};

export const electricityValidationSchemaFields = (
  hasCes: boolean,
  homeUser?: boolean
) => ({
  connectionType: Yup.string().required("Please select"),
  meterProvider: Yup.string().when(["state"], {
    is: (state: State) => state !== State.VIC && !hasCes,
    then: (schema) => schema.required("Please select"),
  }),
  maxLoad: Yup.number()
    .typeError("Please enter a number")
    .min(1.01, "Enter a value greater than 1")
    .required("Enter a value greater than 1"),
  truckAppointmentReason: Yup.string().when("truckAppointment", {
    is: true,
    then: (schema) => schema.required("Please fill"),
  }),
  nmi: Yup.string().when("premiseAddress", {
    is: (premiseAddress: PremiseAddressValues) =>
      getIsNmiRequired(premiseAddress.postcode),
    then: (schema) =>
      schema
        .required("Oops, you must enter a NMI.")
        .matches(
          /^(?:\d{10}|[A-Za-z\d]{10})$/,
          "Oops, NMI must be 10 characters"
        ),
    otherwise: (schema) =>
      schema
        .transform((value) => (value === "" ? null : value))
        .nullable()
        .matches(
          /^(?:\d{10}|[A-Za-z\d]{10})$/,
          "Oops, NMI must be 10 characters"
        ),
  }),
  phases: Yup.string().required("Please select"),
  installationType: Yup.string().required("Please select"),
  otherLocationDescription: Yup.string().when("locationDescription", {
    is: "other",
    then: (schema) => schema.required("Please fill"),
  }),
  sizeOfSolarSystem: Yup.string().when("solar", {
    is: "yes",
    then: (schema) =>
      schema
        .matches(numberStringOnly, {
          message: "Numbers only",
        })
        .required("Please fill"),
  }),
  otherOffpeakRequirement: Yup.string().when("offpeakRequirements", {
    is: (offpeakRequirements: string[]) =>
      offpeakRequirements.includes("other"),
    then: (schema) => schema.required("Please fill"),
  }),
  rexId: Yup.string().when("state", {
    is: (state: string) => state === "SA",
    then: (schema) =>
      schema.matches(numberStringOnly, "Numbers only").required("Numbers only"),
  }),
  coes: Yup.string().when(["state", "truckAppointment"], {
    is: (state: string, truckAppointment: boolean) =>
      state === "VIC" && !truckAppointment,
    then: (schema) =>
      schema
        .matches(alphanumericOnly, "Numbers and letters Only")
        .required("Numbers and letters Only"),
  }),
  coesFiles: Yup.array().when(["state", "truckAppointment"], {
    is: (state: string, truckAppointment: boolean) =>
      state === "VIC" && !truckAppointment,
    then: (schema) =>
      schema
        .min(1, "Please upload a document")
        .required("Please upload a document"),
  }),
  ewrFiles: Yup.array().when("state", {
    is: (state: string) => state === "VIC",
    then: (schema) =>
      schema
        .min(1, "Please upload a document")
        .required("Please upload a document"),
  }),
  electricityLifeSupport: Yup.boolean()
    .nullable()
    .transform((value) => (value === undefined ? null : value))
    .default(null)
    .when(["electricityAccountPayer", "state"], {
      is: (electricityAccountPayer: string, state: string) =>
        electricityAccountPayer === AccountPayer.COMPANY &&
        !homeUser &&
        state === State.VIC,
      then: (schema) =>
        schema.required(
          "Oops, you must let us know if someone at the property is  on life support."
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
});

export const electricityValidationSchema = (hasCes: boolean) =>
  Yup.object().shape({
    ...electricityValidationSchemaFields(hasCes),
  });
export const supervisorValidationSchema = Yup.object().shape({
  first_name: Yup.string()
    .matches(nameRegex, "Letters, hyphens spaces and apostrophes only")
    .required("Letters, hyphens spaces and apostrophes only"),
  last_name: Yup.string()
    .matches(nameRegex, "Letters, hyphens spaces and apostrophes only")
    .required("Please fill"),
  phone_number: Yup.string()
    .test({
      message: "Please enter a valid 10 digit phone number",
      test: (value) => validPhoneNumber(value as string),
    })
    .matches(numberStringOnly, "Please enter a valid 10 digit phone number")
    .required("Please enter a valid 10 digit phone number"),
  email: Yup.string().email("Please enter a valid email address").optional(),
});

export const electricianValidationSchema = Yup.object().shape({
  first_name: Yup.string()
    .matches(nameRegex, "Letters, hyphens spaces and apostrophes only")
    .required("Letters, hyphens spaces and apostrophes only"),
  last_name: Yup.string()
    .matches(nameRegex, "Letters, hyphens spaces and apostrophes only")
    .required("Please fill"),
  license_number: Yup.string()
    .matches(alphanumericOnly, "Numbers and letters only")
    .required("Numbers and letters only"),
  phone_number: Yup.string()
    .test({
      message: "Please enter a valid 10 digit phone number",
      test: (value) => validPhoneNumber(value as string),
    })
    .matches(numberStringOnly, "Please enter a valid 10 digit phone number")
    .required("Please enter a valid 10 digit phone number"),
  email: Yup.string().email("Please enter a valid email address").optional(),
});

export const plumberValidationSchema = Yup.object().shape({
  first_name: Yup.string()
    .matches(nameRegex, "Letters, hyphens spaces and apostrophes only")
    .required("Letters, hyphens spaces and apostrophes only"),
  last_name: Yup.string()
    .matches(nameRegex, "Letters, hyphens spaces and apostrophes only")
    .required("Please fill"),
  license_number: Yup.string().when("$state", {
    is: (state: string) => state === "VIC",
    then: (schema) =>
      schema
        .required(
          "For Victoria, licence number should be between 8000 and 130000"
        )
        .matches(
          /^\d{4,6}$/,
          "For Victoria, licence number should be between 8000 and 130000"
        )
        .test(
          "range",
          "For Victoria, licence number should be between 8000 and 130000",
          (value) => {
            if (!value) return false;
            const num = parseInt(value, 10);
            return num >= 8000 && num <= 130000;
          }
        ),
    otherwise: (schema) =>
      schema
        .matches(alphanumericOnly, "Numbers and letters only")
        .required("Numbers and letters only"),
  }),
  phone_number: Yup.string()
    .test({
      message: "Please enter a valid 10 digit phone number",
      test: (value) => validPhoneNumber(value as string),
    })
    .matches(numberStringOnly, "Please enter a valid 10 digit phone number")
    .required("Please enter a valid 10 digit phone number"),
  email: Yup.string().email("Please enter a valid email address").optional(),
});

export const serviceProviderValidationSchema = Yup.object().shape({
  first_name: Yup.string()
    .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
    .required("Letters, hyphens, spaces and apostrophes only"),
  last_name: Yup.string()
    .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
    .required("Letters, hyphens, spaces and apostrophes only"),
  license_number: Yup.string()
    .when("meterProvider", {
      is: (meterProvider: string) => meterProvider === "ACUMENMPB",
      then: (schema) =>
        schema
          .matches(
            accreditationNumberIntelliHubRegex,
            "Can have no more than 6 letters or digits"
          )
          .required("Please fill"),
    })
    .when("meterProvider", {
      is: (meterProvider: string) => meterProvider === "TCAMPMPB",
      then: (schema) =>
        schema
          .matches(
            accreditationNumberPlusESRegex,
            "Can have no more than 8 letters or digit"
          )
          .required("Please fill"),
    }),
});

export const accountBlockInitialValues = {
  accountHolderDetailsType: "online",
  accountId: null,
  accountHolder: "person",
  secondaryAccountHolder: false,
  abn: "",
  accountPrimaryFirstName: "",
  accountPrimaryLastName: "",
  accountPrimaryDob: null,
  accountPrimaryPhone: "",
  accountPrimaryEmail: "",
  accountCommsPrefs: "post",
  accountSecondaryFirstName: "",
  accountSecondaryLastName: "",
  accountSecondaryDob: null,
  accountSecondaryPhone: "",
  accountSecondaryEmail: "",
  addSecondaryNameToBill: false,
  sameAsConnectionAddress: false,
  customerConsentFiles: [],
  claimsCustomerConsentFiles: [],
  "billing-address": {
    suburb: "",
    state: "",
    postcode: "",
    pobox: false,
    unitNumber: "",
    premiseType: "",
    streetNumber: "",
    streetName: "",
    level: "",
    streetSuffix: "",
    streetType: "",
    poboxNumber: "",
    poboxType: "",
    buildingName: "",
  },
  billingAddressString: "",
  homeUserAgreeTerms: false,
};

export const formInitValues = {
  //premise block
  id: null,
  locId: "",
  siteSupervisor: "",
  siteSupervisors: [],
  licensedPlumbers: [],
  premiseAddressString: "",
  requestingCompany: null,
  projectName: "",
  projectId: "",
  propertyType: "RESIDENTIAL",
  premiseAddress: {
    siteId: "",
    state: "",
    buildingName: "",
    suburb: "",
    postcode: "",
    unitNumber: "",
    premiseType: "",
    streetNumber: "",
    lotNumber: "",
    streetName: "",
    grannyFlat: false,
    level: "",
    dpNumber: "",
    streetSuffix: "",
    streetType: "",
    sectionNumber: "",
    blockNumber: "",
  },
  propertyAccess: "yes",
  preventingAccess: "",
  serviceAssignee: "",
  selectedConnections: [],
  //account block
  ...accountBlockInitialValues,

  //electricity block
  ...electricityInitialValues,
  //gas block
  ...gasInitialValues,
  //close transfer block
  ...internetInitValues,
  closeTransfer: closeTransferInitialValues,
  internetRequestStatus: null,
};

export const formValidationSchema = (
  hasCes: boolean,
  isAccountReadOnly?: boolean,
  isDigitalEic?: boolean | false
) =>
  Yup.object().shape({
    //premise block
    projectName: Yup.string(),
    propertyType: Yup.string(),
    propertyAccess: Yup.string(),
    preventingAccess: Yup.string(),
    assignRequirements: Yup.boolean(),
    serviceAssignee: Yup.string(),
    electricityCollaboratorId: Yup.string().when(
      "assignElectricityRequirements",
      {
        is: (assignElectricityRequirements: boolean) =>
          assignElectricityRequirements,
        then: (schema) => schema.required("Please select"),
      }
    ),
    gasCollaboratorId: Yup.string().when("assignGasRequirements", {
      is: (assignGasRequirements: boolean) => assignGasRequirements,
      then: (schema) => schema.required("Please select"),
    }),
    //account block
    ...(isDigitalEic
      ? onlineFormAccountDetailsValidationSchemaFields()
      : accountValidationSchemaFields("electricity", false, isAccountReadOnly)),
    //gas line block
    // ...gasLineValidationSchema.fields,
    //electricity block
    ...electricityValidationSchemaFields(hasCes),
    ...electricitySubmissionWithCompanyFields,
  });

export const homeUserFormValidationSchema = (
  hasCes: boolean,
  isAccountReadOnly?: boolean,
  isDigitalEic?: boolean | false
) =>
  Yup.object().shape({
    //premise block
    projectName: Yup.string(),
    propertyType: Yup.string(),
    propertyAccess: Yup.string(),
    preventingAccess: Yup.string(),
    assignRequirements: Yup.boolean(),
    serviceAssignee: Yup.string(),
    electricityCollaboratorId: Yup.string().when(
      "assignElectricityRequirements",
      {
        is: (assignElectricityRequirements: boolean) =>
          assignElectricityRequirements,
        then: (schema) => schema.required("Please select"),
      }
    ),
    gasCollaboratorId: Yup.string().when("assignGasRequirements", {
      is: (assignGasRequirements: boolean) => assignGasRequirements,
      then: (schema) => schema.required("Please select"),
    }),
    //account block
    ...homeUserAccountValidationSchemaFields(
      "electricity",
      false,
      isAccountReadOnly,
      isDigitalEic
    ),
    homeUserAgreeTerms: Yup.boolean().when(["electricityAccountPayer"], {
      is: (electricityAccountPayer: string) => {
        return (
          !isDigitalEic &&
          electricityAccountPayer !== AccountPayer.SOMEONE_ELSE &&
          !isAccountReadOnly
        );
      },
      then: (schema) =>
        schema.isTrue("Oops, you must agree to the terms and conditions."),
      otherwise: (schema) => schema.notRequired(),
    }),
    //gas line block
    // ...gasLineValidationSchema.fields,
    //electricity block
    ...electricityValidationSchemaFields(hasCes, true),
    ...electricitySubmissionHomeUserFields,
  });

const formGasCommonValidationSchema = {
  //premise block
  projectName: Yup.string(),
  propertyType: Yup.string(),
  propertyAccess: Yup.string(),
  preventingAccess: Yup.string(),
  assignRequirements: Yup.boolean(),
  serviceAssignee: Yup.string(),
  electricityCollaboratorId: Yup.string().when(
    "assignElectricityRequirements",
    {
      is: (assignElectricityRequirements: boolean) =>
        assignElectricityRequirements,
      then: (schema) => schema.required("Please select"),
    }
  ),
  gasCollaboratorId: Yup.string().when("assignGasRequirements", {
    is: (assignGasRequirements: boolean) => assignGasRequirements,
    then: (schema) => schema.required("Please select"),
  }),
};

export const formGasLineWithCompanyValidationSchema = (
  isAccountReadOnly?: boolean,
  isDigitalEic?: boolean | false
) =>
  Yup.object().shape({
    //account block
    ...(isDigitalEic
      ? onlineFormAccountDetailsValidationSchemaFields()
      : accountValidationSchemaFields("gasLine", false, isAccountReadOnly)),
    //common part
    ...formGasCommonValidationSchema,
    siteSupervisor: Yup.string().required("Please select"),
    gasLine: Yup.object().shape({
      ...gasLineValidationBaseSchema(false),
      licensedPlumber: Yup.string().when("$state", {
        is: (state: string) => {
          return state === "NSW";
        },
        then: (schema) => schema.required("Please select"),
      }),
    }),
  });
export const formGasLineHomeUserValidationSchema = (
  isAccountReadOnly?: boolean,
  isDigitalEic?: boolean | false
) =>
  Yup.object().shape({
    //account block
    ...accountValidationSchemaFields(
      "gasLine",
      false,
      isAccountReadOnly,
      isDigitalEic
    ),
    // homeUserAgreeTerms: isAccountReadOnly
    //   ? Yup.boolean().notRequired()
    //   : Yup.boolean().isTrue(
    //       "Oops, you must agree to the terms and conditions."
    //     ),

    homeUserAgreeTerms: Yup.boolean().when(
      ["accountHolder", "gasLine.accountPayer"],
      {
        is: (accountHolder: string, accountPayer: string) =>
          !isDigitalEic &&
          accountPayer !== AccountPayer.SOMEONE_ELSE &&
          (accountHolder === "company" || accountHolder === "person") &&
          !isAccountReadOnly,
        then: (schema) =>
          schema.isTrue("Oops, you must agree to the terms and conditions."),
        otherwise: (schema) => schema.notRequired(),
      }
    ),
    //common part
    ...formGasCommonValidationSchema,
    gasLine: Yup.object().shape({
      ...gasLineValidationBaseSchema(true),
      homeUserLicencedPlumber: Yup.object().when("$state", {
        is: (state: string) => {
          return ["NSW"].includes(state);
        },
        then: (schema) =>
          schema.shape({
            firstName: Yup.string()
              .matches(
                nameRegex,
                "Letters, hyphens, spaces and apostrophes only"
              )
              .required("Letters, hyphens, spaces and apostrophes only"),
            lastName: Yup.string()
              .matches(
                nameRegex,
                "Letters, hyphens, spaces and apostrophes only"
              )
              .required("Letters, hyphens, spaces and apostrophes only"),
            licenseNumber: Yup.string().when("$state", {
              is: (state: string) => {
                return ["VIC"].includes(state);
              },
              then: (innerSchema) =>
                innerSchema
                  .test({
                    message: "Please enter a valid plumber license number",
                    test: async (value) => {
                      if (!value) return false;
                      const { valid } = await validatePlumberLicense(
                        value as string
                      );
                      return valid;
                    },
                  })
                  .required("Numbers and letters only"),
              otherwise: (innerSchema) =>
                innerSchema.required("Numbers and letters only"),
            }),
            phoneNumber: Yup.string()
              .test({
                message: "Please enter a valid 10 digit phone number",
                test: (value) => validPhoneNumber(value as string),
              })
              .matches(
                numberStringOnly,
                "Please enter a valid 10 digit phone number"
              )
              .required("Please enter a valid 10 digit phone number"),
            email: Yup.string()
              .email("Please enter a valid email address")
              .optional(),
          }),
        otherwise: (schema) => schema.notRequired(),
      }),
      homeUserSiteSupervisor: Yup.object().when("$state", {
        is: (state: string) => {
          return ["NSW", "QLD", "VIC", "SA"].includes(state);
        },
        then: (schema) =>
          schema.shape({
            firstName: Yup.string()
              .matches(
                nameRegex,
                "Letters, hyphens, spaces and apostrophes only"
              )
              .required("Letters, hyphens, spaces and apostrophes only"),
            lastName: Yup.string()
              .matches(
                nameRegex,
                "Letters, hyphens, spaces and apostrophes only"
              )
              .required("Letters, hyphens, spaces and apostrophes only"),
            phoneNumber: Yup.string()
              .test({
                message: "Please enter a valid 10 digit phone number",
                test: (value) => validPhoneNumber(value as string),
              })
              .matches(
                numberStringOnly,
                "Please enter a valid 10 digit phone number"
              )
              .required("Please enter a valid 10 digit phone number"),
            email: Yup.string()
              .email("Please enter a valid email address")
              .optional(),
          }),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  });

const gasMeterStreetNumberValidationSchema = {
  streetNumber: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .matches(
      numbersLettersHyphensSpacesNotStartWithHyphen,
      "Numbers, letters, hyphens and spaces only"
    ),
};

export const formGasBothWithCompanyValidationSchema = (
  isAccountReadOnly?: boolean,
  isDigitalEic?: boolean | false
) =>
  Yup.object().shape({
    //account block
    ...(isDigitalEic
      ? onlineFormAccountDetailsValidationSchemaFields()
      : accountValidationSchemaFields("both", false, isAccountReadOnly)),
    //common part
    ...formGasCommonValidationSchema,
    siteSupervisor: Yup.string().required("Please select"),
    premiseAddress: Yup.object().shape({
      ...gasMeterStreetNumberValidationSchema,
    }),
    both: Yup.object().shape({
      ...gasBothValidationBaseSchema(false),
      licensedPlumber: Yup.string().when("$state", {
        is: (state: string) => {
          return ["ACT", "QLD"].includes(state);
        },
        then: (schema) => schema.required("Please select"),
      }),
    }),
  });
export const formGasBothHomeUserValidationSchema = (
  isAccountReadOnly?: boolean,
  isDigitalEic?: boolean | false
) =>
  Yup.object().shape({
    //account block
    ...homeUserAccountValidationSchemaFields(
      "both",
      false,
      isAccountReadOnly,
      isDigitalEic
    ),
    homeUserAgreeTerms: Yup.boolean().when(
      ["accountHolder", "both.accountPayer"],
      {
        is: (accountHolder: string, accountPayer: string) =>
          !isDigitalEic &&
          accountPayer !== AccountPayer.SOMEONE_ELSE &&
          (accountHolder === "company" || accountHolder === "person") &&
          !isAccountReadOnly,
        then: (schema) =>
          schema.isTrue("Oops, you must agree to the terms and conditions."),
        otherwise: (schema) => schema.notRequired(),
      }
    ),
    //common part
    ...formGasCommonValidationSchema,
    premiseAddress: Yup.object().shape({
      ...gasMeterStreetNumberValidationSchema,
    }),

    both: Yup.object().shape({
      ...gasBothValidationBaseSchema(true),
      ...homeUserGasBlockRelationshipsValidationSchema,
    }),
  });

export const formGasMeterWithCompanyValidationSchema = (
  isAccountReadOnly?: boolean,
  isDigitalEic?: boolean | false
) =>
  Yup.object().shape({
    //account block
    ...(isDigitalEic
      ? onlineFormAccountDetailsValidationSchemaFields()
      : accountValidationSchemaFields("gasMeter", false, isAccountReadOnly)),
    //common part
    ...formGasCommonValidationSchema,
    premiseAddress: Yup.object().shape({
      ...gasMeterStreetNumberValidationSchema,
    }),
    gasMeter: Yup.object().shape({
      ...gasMeterValidationBaseSchema(false),
      licensedPlumber: Yup.string().required("Please select"),
    }),
  });

export const formGasMeterHomeUserValidationSchema = (
  isAccountReadOnly?: boolean,
  isDigitalEic?: boolean | false
) =>
  Yup.object().shape({
    //account block
    ...homeUserAccountValidationSchemaFields(
      "gasMeter",
      false,
      isAccountReadOnly,
      isDigitalEic
    ),
    homeUserAgreeTerms: Yup.boolean().when(
      ["accountHolder", "gasMeter.accountPayer"],
      {
        is: (accountHolder: string, gasMeterAccountPayer: string) =>
          !isDigitalEic &&
          gasMeterAccountPayer !== AccountPayer.SOMEONE_ELSE &&
          (accountHolder === "company" || accountHolder === "person") &&
          !isAccountReadOnly,
        then: (schema) =>
          schema.isTrue("Oops, you must agree to the terms and conditions."),
        otherwise: (schema) => schema.notRequired(),
      }
    ),
    //common part
    ...formGasCommonValidationSchema,
    premiseAddress: Yup.object().shape({
      ...gasMeterStreetNumberValidationSchema,
    }),
    gasMeter: Yup.object().shape({
      ...gasMeterValidationBaseSchema(true),
      homeUserLicencedPlumber: Yup.object().when("$state", {
        is: (state: string) => {
          return ["NSW", "QLD", "VIC", "SA"].includes(state);
        },
        then: (schema) =>
          schema.shape({
            firstName: Yup.string()
              .matches(
                nameRegex,
                "Letters, hyphens, spaces and apostrophes only"
              )
              .required("Letters, hyphens, spaces and apostrophes only"),
            lastName: Yup.string()
              .matches(
                nameRegex,
                "Letters, hyphens, spaces and apostrophes only"
              )
              .required("Letters, hyphens, spaces and apostrophes only"),
            licenseNumber: Yup.string().when("$state", {
              is: (state: string) => {
                return ["VIC"].includes(state);
              },
              then: (innerSchema) =>
                innerSchema
                  .test({
                    message: "Please enter a valid plumber license number",
                    test: async (value) => {
                      if (!value) return false;
                      const { valid } = await validatePlumberLicense(
                        value as string
                      );
                      return valid;
                    },
                  })
                  .required("Numbers and letters only"),
              otherwise: (innerSchema) =>
                innerSchema.required("Numbers and letters only"),
            }),
            phoneNumber: Yup.string()
              .test({
                message: "Please enter a valid 10 digit phone number",
                test: (value) => validPhoneNumber(value as string),
              })
              .matches(
                numberStringOnly,
                "Please enter a valid 10 digit phone number"
              )
              .required("Please enter a valid 10 digit phone number"),
            email: Yup.string()
              .email("Please enter a valid email address")
              .optional(),
          }),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  });

export const homeUserGasBlockRelationshipsValidationSchema = {
  homeUserLicencedPlumber: Yup.object().when("$state", {
    is: (state: string) => {
      return ["NSW", "ACT", "QLD"].includes(state);
    },
    then: (schema) =>
      schema.shape({
        firstName: Yup.string()
          .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
          .required("Letters, hyphens, spaces and apostrophes only"),
        lastName: Yup.string()
          .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
          .required("Letters, hyphens, spaces and apostrophes only"),
        licenseNumber: Yup.string().when("$state", {
          is: (state: string) => {
            return ["VIC"].includes(state);
          },
          then: (innerSchema) =>
            innerSchema
              .test({
                message: "Please enter a valid plumber license number",
                test: async (value) => {
                  if (!value) return false;
                  const { valid } = await validatePlumberLicense(
                    value as string
                  );
                  return valid;
                },
              })
              .required("Numbers and letters only"),
          otherwise: (innerSchema) =>
            innerSchema.required("Numbers and letters only"),
        }),
        phoneNumber: Yup.string()
          .test({
            message: "Please enter a valid 10 digit phone number",
            test: (value) => validPhoneNumber(value as string),
          })
          .matches(
            numberStringOnly,
            "Please enter a valid 10 digit phone number"
          )
          .required("Please enter a valid 10 digit phone number"),
        email: Yup.string()
          .email("Please enter a valid email address")
          .optional(),
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
  homeUserSiteSupervisor: Yup.object().when("$state", {
    is: (state: string) => {
      return ["NSW", "ACT", "QLD"].includes(state);
    },
    then: (schema) =>
      schema.shape({
        firstName: Yup.string()
          .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
          .required("Letters, hyphens, spaces and apostrophes only"),
        lastName: Yup.string()
          .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
          .required("Letters, hyphens, spaces and apostrophes only"),
        phoneNumber: Yup.string()
          .test({
            message: "Please enter a valid 10 digit phone number",
            test: (value) => validPhoneNumber(value as string),
          })
          .matches(
            numberStringOnly,
            "Please enter a valid 10 digit phone number"
          )
          .required("Please enter a valid 10 digit phone number"),
        email: Yup.string()
          .email("Please enter a valid email address")
          .optional(),
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
};
const closeTransferPreferDateSchema = (schema: Yup.Schema) =>
  schema
    .nullable()
    .required("Please specify when the close/transfer is required")
    .test(
      "is-valid-date",
      "Please enter in correct format DD/MM/YYYY",
      (value: Date | null) => {
        if (value === null) return false; // Fail if null (required)
        return value instanceof Date && !isNaN(value.getTime());
      }
    )
    .test(
      "is-not-past-date",
      "The date should be today's date or a future date",
      (value: Date | null) => {
        if (value === null) return false; // Fail if null (required)
        const today = startOfDay(new Date());
        return value instanceof Date && !isBefore(value, today);
      }
    );

export const formCloseTransferValidationSchema = () =>
  Yup.object().shape({
    closeTransfer: Yup.object().shape({
      elecPreferDate: Yup.mixed().when(
        ["serviceTypes", "$isElectricityReadOnly", "$isElectricityEnable"],
        {
          is: (
            serviceTypes: ServiceTypeEnum[],
            isElectricityReadOnly: boolean,
            isElectricityEnable: boolean
          ) =>
            serviceTypes.includes(ServiceTypeEnum.ELECTRICITY) &&
            !isElectricityReadOnly &&
            isElectricityEnable,
          then: closeTransferPreferDateSchema,
          otherwise: (schema) => schema.nullable().notRequired(),
        }
      ),
      gasPreferDate: Yup.mixed().when(
        ["serviceTypes", "$isGasReadOnly", "$isGasEnable"],
        {
          is: (
            serviceTypes: ServiceTypeEnum[],
            isGasReadOnly: boolean,
            isGasEnable: boolean
          ) =>
            serviceTypes.includes(ServiceTypeEnum.GAS) &&
            !isGasReadOnly &&
            isGasEnable,
          then: closeTransferPreferDateSchema,
          otherwise: (schema) => schema.nullable().notRequired(),
        }
      ),
      accountHolder: Yup.object().when("$handoverType", {
        is: (handoverType: HandoverTypeEnum) => {
          return ["CLOSE_AND_TRANSFER"].includes(handoverType);
        },
        then: (schema) =>
          schema.shape({
            firstName: Yup.string()
              .trim()
              .matches(
                nameRegex,
                "Letters, hyphens, spaces and apostrophes only"
              )
              .required("Letters, hyphens, spaces and apostrophes only"),
            lastName: Yup.string()
              .trim()
              .matches(
                nameRegex,
                "Letters, hyphens, spaces and apostrophes only"
              )
              .required("Letters, hyphens, spaces and apostrophes only"),
            email: Yup.string()
              .matches(emailRegex, "Please enter a valid email address")
              .required("Please enter a valid email address"),
          }),
        otherwise: (schema) => schema.notRequired(),
      }),

      // signedTransferFormFiles: Yup.array().when(["handoverType", "fileMeta"], {
      //   is: (handoverType: HandoverTypeEnum, fileMeta: UploadedFile) =>
      //     handoverType === HandoverTypeEnum.CLOSE_AND_TRANSFER && !fileMeta,
      //   then: (schema) =>
      //     schema.min(1, "Please attach").required("Please attach"),
      //   otherwise: (schema) => schema.nullable().notRequired(),
      // }),
      serviceTypes: Yup.array().min(1),
      isCustomerConsentChecked: Yup.boolean().when(
        ["$accountPayers", "$handoverType", "$isShowCustomerConsentCheckbox"],
        {
          is: (
            accountPayers: string,
            handoverType: HandoverTypeEnum,
            isShowCustomerConsentCheckbox: boolean
          ) =>
            (accountPayers.includes(AccountPayer.SOMEONE_ELSE) ||
              isShowCustomerConsentCheckbox) &&
            handoverType === HandoverTypeEnum.CLOSE_AND_TRANSFER,
          then: (schema) =>
            schema.test("is-true", "Must be true", (value) => value === true),
          otherwise: (schema) => schema.notRequired(),
        }
      ),
    }),
  });

const internetDateSchema = (schema: Yup.Schema) =>
  schema
    .nullable()
    .required("Oops, you need to enter a handover date.")
    .test(
      "is-valid-date",
      "Oops, you need to enter a valid date.",
      (value: Date | null) => {
        const dateVal = parseDateString(value === null ? "" : value.toString());
        if (dateVal === null) return false; // Fail if null (required)
        return dateVal instanceof Date && !isNaN(dateVal.getTime());
      }
    )
    .test(
      "is-not-past-date",
      "Oops, you need to enter a date in future.",
      (value: Date | null) => {
        if (value === null) return false; // Fail if null (required)
        const dateVal = parseDateString(value === null ? "" : value.toString());
        const today = startOfDay(new Date());
        return dateVal instanceof Date && !isBefore(dateVal, today);
      }
    );

export const internetSubmissionValidation = {
  internetHandoverDate: internetDateSchema(Yup.mixed()),
  internetTerms: Yup.boolean().when(["internetAccountPayer"], {
    is: (internetAccountPayer: string) =>
      internetAccountPayer === AccountPayer.SOMEONE_ELSE,
    then: (schema) =>
      schema
        .required("Oops, you must agree to the terms and conditions.")
        .isTrue("Oops, you must agree to the terms and conditions."),
    otherwise: (schema) => schema.notRequired(),
  }),
};
export const homeUserFormValSchemaInternet = (
  isAccountReadOnly?: boolean,
  isDigitalEic?: boolean | false
) =>
  Yup.object().shape({
    //premise block
    ...formGasCommonValidationSchema, // Actually it is common validation for premise block
    //account block
    ...homeUserAccountValidationSchemaFields(
      "internet",
      false,
      isAccountReadOnly,
      isDigitalEic
    ),
    homeUserAgreeTerms: Yup.boolean().when(["internetAccountPayer"], {
      is: (internetAccountPayer: string) => {
        return (
          !isDigitalEic &&
          internetAccountPayer !== AccountPayer.SOMEONE_ELSE &&
          !isAccountReadOnly
        );
      },
      then: (schema) =>
        schema.isTrue("Oops, you must agree to the terms and conditions."),
      otherwise: (schema) => schema.notRequired(),
    }),
    ...internetSubmissionValidation,
  });

export const formValidationSchemaInternet = (
  isAccountReadOnly?: boolean,
  isDigitalEic?: boolean | false
) =>
  Yup.object().shape({
    //premise block
    ...formGasCommonValidationSchema, // Actually it is common validation for premise block
    siteSupervisor: Yup.string().required("Please select"),
    //account block
    ...(isDigitalEic
      ? onlineFormAccountDetailsValidationSchemaFields()
      : accountValidationSchemaFields("internet", false, isAccountReadOnly)),
    ...internetSubmissionValidation,
  });
/**
 * Checks if two premises are equal based on selected address parts
 * Only compares the fields specified in selectedAddressParts (editable fields)
 * @param premise1 - First premise to compare
 * @param premise2 - Second premise to compare
 * @param selectedAddressParts - Array of field names that are editable/selected
 * @returns true if premises are equal, false otherwise
 */
const arePremisesEqual = (
  premise1: PremiseComparable,
  premise2: PremiseComparable,
  selectedAddressParts: string[]
): boolean => {
  // Compare only the selected (editable) fields
  const editableMatch = selectedAddressParts.every((field) => {
    const value1 = premise1[field as keyof PremiseComparable]?.trim() || "";
    const value2 = premise2[field as keyof PremiseComparable]?.trim() || "";
    return !!value1 && !!value2 && value1 === value2;
  });

  return editableMatch;
};

/**
 * Field validation rules mapping for bulk duplicate premises
 * Maps PremiseComparable field names to their validation patterns
 */
const fieldValidationRules: {
  [key: string]: { pattern: RegExp; message: string };
} = {
  unit_number: {
    pattern: alphanumericOnly,
    message: "Oops, unit number should be numbers and letters only",
  },
  floor_number: {
    pattern: /^[A-Za-z]*\d+(\.\d+)?[A-Za-z]*$/,
    message: "Oops, level should be numbers, letters and dots only",
  },
  street_number: {
    pattern: numbersLettersHyphensSpacesNotStartWithHyphen,
    message:
      "Oops, street number should be numbers, letters, hyphens and spaces only",
  },
  lot_number: {
    pattern: alphanumericOnly,
    message: "Oops, Lot number should be numbers and letters only",
  },
  section_number: {
    pattern: numberStringOnly,
    message: "Oops, section number should be numbers only",
  },
  dp_number: {
    pattern: numberStringOnly,
    message: "Oops, dp number should be numbers only",
  },
  block_number: {
    pattern: numberStringOnly,
    message: "Oops, block number should be numbers only",
  },
  street_name: {
    pattern: suburbRegex,
    message:
      "Oops, street name should be numbers, letters, hyphens and spaces only",
  },
};

/**
 * Validates bulk duplicate premises:
 * 1. Ensures all editable fields are filled
 * 2. Validates field formats according to schema rules
 * 3. Checks no two premises are duplicates
 * 4. Verifies no premise matches the original
 * @param premises - Array of premises to validate
 * @param selectedAddressParts - Array of field names that user can edit
 * @param originalPremise - The original premise object to compare against
 * @returns Object with isValid flag, errors object with error type per premise, field errors, and error message
 */
export const validateBulkDuplicatePremises = (
  premises: PremiseComparable[],
  selectedAddressParts: string[],
  originalPremise: OriginalPremiseFormat
): {
  isValid: boolean;
  errors: {
    [key: number]: Array<string>;
  };
  fieldErrors: {
    [premiseIndex: number]: {
      [fieldName: string]: string;
    };
  };
} => {
  const errors: {
    [key: number]: Array<string>;
  } = {};
  const fieldErrors: {
    [premiseIndex: number]: {
      [fieldName: string]: string;
    };
  } = {};
  let isValid = true;

  // Convert original premise to comparable format
  const originalFormatted: PremiseComparable = {
    unit_number: originalPremise.unit_number?.trim() || "",
    floor_number: originalPremise.floor_number?.trim() || "",
    street_number: originalPremise.site_details?.street_number?.trim() || "",
    lot_number: originalPremise.site_details?.lot_number?.trim() || "",
    section_number: originalPremise.site_details?.section_number?.trim() || "",
    dp_number: originalPremise.site_details?.dp_number?.trim() || "",
    block_number: originalPremise.site_details?.block_number?.trim() || "",
    street_name: originalPremise.site_details?.street_name?.trim() || "",
  };

  // First pass: check for empty fields and format validation
  for (let i = 0; i < premises.length; i++) {
    let hasEmptyField = false;
    fieldErrors[i] = {};
    errors[i] = [];

    // Check each selected address part to ensure it's filled and valid
    for (const field of selectedAddressParts) {
      const value = premises[i][field as keyof PremiseComparable];
      if (!value || value.trim() === "") {
        hasEmptyField = true;
      } else {
        // Validate format for fields that have validation rules
        const validationRule = fieldValidationRules[field];
        if (validationRule && !validationRule.pattern.test(value.trim())) {
          fieldErrors[i][field] = validationRule.message;
          errors[i].push(validationRule.message);
          isValid = false;
        }
      }
    }

    if (hasEmptyField) {
      errors[i].unshift(
        "Oops, there are one or more missing numbers from this address."
      );
      isValid = false;
    } else if (
      arePremisesEqual(premises[i], originalFormatted, selectedAddressParts)
    ) {
      errors[i].push(
        "Oops, address cannot be the same as the premise being duplicated."
      );
      isValid = false;
    }
  }

  // Second pass: check for duplicates among premises (mark only the last occurrence)
  for (let i = 0; i < premises.length; i++) {
    // Skip if already has an error (empty, invalid format, or duplicate with original)
    // Find all matching premises after this one
    const duplicateIndices: number[] = [];
    for (let j = i + 1; j < premises.length; j++) {
      // Only check premises without errors
      if (arePremisesEqual(premises[i], premises[j], selectedAddressParts)) {
        duplicateIndices.push(j);
      }
    }

    // If duplicates found, mark only the last one
    if (duplicateIndices.length > 0) {
      const lastDuplicateIndex = duplicateIndices[duplicateIndices.length - 1];
      errors[lastDuplicateIndex].push(
        "Oops, all new premises addresses must be unique."
      );
      isValid = false;
    }
  }
  return { isValid, errors, fieldErrors };
};

export const validateBulkDuplicatePremisesPerRow = (
  premises: PremiseComparable[],
  selectedAddressParts: string[],
  originalPremise: OriginalPremiseFormat,
  rowIndex: number
): {
  isValid: boolean;
  errors: {
    [key: number]: Array<string>;
  };
  fieldErrors: {
    [premiseIndex: number]: {
      [fieldName: string]: boolean;
    };
  };
} => {
  const errors: {
    [key: number]: Array<string>;
  } = {};
  const fieldErrors: {
    [premiseIndex: number]: {
      [fieldName: string]: boolean;
    };
  } = {};
  let isValid = true;

  // Convert original premise to comparable format
  const originalFormatted: PremiseComparable = {
    unit_number: originalPremise.unit_number?.trim() || "",
    floor_number: originalPremise.floor_number?.trim() || "",
    street_number: originalPremise.site_details?.street_number?.trim() || "",
    lot_number: originalPremise.site_details?.lot_number?.trim() || "",
    section_number: originalPremise.site_details?.section_number?.trim() || "",
    dp_number: originalPremise.site_details?.dp_number?.trim() || "",
    block_number: originalPremise.site_details?.block_number?.trim() || "",
    street_name: originalPremise.site_details?.street_name?.trim() || "",
  };

  // First pass: check for empty fields and format validation
  let hasEmptyField = false;
  fieldErrors[rowIndex] = {};
  errors[rowIndex] = [];

  // Check each selected address part to ensure it's filled and valid
  for (const field of selectedAddressParts) {
    const value = premises[rowIndex][field as keyof PremiseComparable];
    if (!value || value.trim() === "") {
      hasEmptyField = true;
    } else {
      // Validate format for fields that have validation rules
      const validationRule = fieldValidationRules[field];
      if (validationRule && !validationRule.pattern.test(value.trim())) {
        fieldErrors[rowIndex][field] = true;
        errors[rowIndex].push(validationRule.message);
        isValid = false;
      }
    }
  }

  if (hasEmptyField) {
    errors[rowIndex].unshift(
      "Oops, there are one or more missing numbers from this address."
    );
    isValid = false;
  }

  // Check if premise matches the original
  if (
    arePremisesEqual(
      premises[rowIndex],
      originalFormatted,
      selectedAddressParts
    )
  ) {
    errors[rowIndex].push(
      "Oops, address cannot be the same as the premise being duplicated."
    );
    isValid = false;
  }

  // Second pass: check for duplicates among premises (mark only the last occurrence)
  const duplicateIndices = premises.some(
    (premise, index) =>
      index !== rowIndex &&
      arePremisesEqual(premises[rowIndex], premise, selectedAddressParts)
  );

  if (duplicateIndices) {
    errors[rowIndex].push("Oops, all new premises addresses must be unique.");
    isValid = false;
  }
  return { isValid, errors, fieldErrors };
};
